import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api, ApiError } from '../api.js';
import { useMe } from '../auth.js';
import { Button, ErrorText } from '../components/ui.js';
import { eur, period, reasonText, VOUCHER_STATUS, type VoucherStatus } from '../format.js';
import { AmountCheck, type PartnerInvoice } from './StatementInvoice.js';

export interface BookkeepingVoucher {
  id: string;
  kind: 'steam_payment' | 'partner_invoice';
  voucherType: string;
  voucherNumber: string;
  status: VoucherStatus;
  voucherId: string | null;
  files: Array<{ documentId: string; fileId: string }>;
  attempts: number;
  lastError: string | null;
  lexwareVoucherStatus: string | null;
  openAmountEur: string | null;
  paidOn: string | null;
  link: string | null;
  createdAt: string;
}

export interface BookkeepingStatus {
  key: { hint: string; updatedAt: string } | null;
  health: 'ok' | 'failing' | 'not_configured';
  state: { lastRunAt: string | null; lastSuccessAt: string | null; lastError: string | null; consecutiveFailures: number; keyRejected: boolean } | null;
  settings: {
    reminderDays: number;
    retentionDays: number;
    bookFrom: string | null;
    sales: { categoryId: string; isDefault: boolean };
    purchase: Partial<Record<'de_standard' | 'kleinunternehmer' | 'reverse_charge', string>>;
    modesInUse: Array<'de_standard' | 'kleinunternehmer' | 'reverse_charge'>;
    valveContactId: string | null;
  };
  gaps: Array<{ code: string; message: string }>;
  vouchers: BookkeepingVoucher[];
  waiting: Array<{ kind: string; id: string; label: string; reason: string }>;
}

interface Candidate {
  id: string;
  number: string | null;
  partnerName: string;
  payoutEur: string;
  coveredMonths: Array<{ year: number; month: number }>;
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.message ?? e.code) : fallback);
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-GB') : 'never');

export function useBookkeepingStatus() {
  return useQuery({ queryKey: ['invoices', 'bookkeeping'], queryFn: () => api<BookkeepingStatus>('GET', '/api/bookkeeping/status'), refetchInterval: 60_000 });
}

/** "Lexware not configured" and failure state, shown where bookkeeping matters. */
export function BookkeepingBanner({ status }: { status: BookkeepingStatus }) {
  if (status.health === 'failing') {
    return (
      <p role="alert" className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
        Lexware bookkeeping is failing{status.state?.keyRejected ? ': Lexware rejected the API key' : ` (${status.state?.consecutiveFailures ?? 0} rounds in a row)`}. {status.state?.lastError}
      </p>
    );
  }
  if (status.gaps.length > 0) {
    return (
      <p role="status" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950">
        Lexware not configured: nothing is booked yet. {status.gaps.map((g) => g.message).join('; ')}. <Link to="/settings" className="underline">Settings</Link>
      </p>
    );
  }
  return null;
}

function OpenItem({ invoice, onChanged }: { invoice: PartnerInvoice; onChanged: () => Promise<unknown> }) {
  const isAdmin = useMe().user?.role === 'admin';
  const [statementId, setStatementId] = useState('');
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const detail = useQuery({
    queryKey: ['invoices', 'detail', invoice.id],
    queryFn: () => api<PartnerInvoice & { candidates: Candidate[] }>('GET', `/api/partner-invoices/${invoice.id}`),
    enabled: isAdmin,
  });
  const assign = useMutation({ mutationFn: () => api('POST', `/api/partner-invoices/${invoice.id}/assign`, { statementId }), onSuccess: onChanged });
  const dismiss = useMutation({ mutationFn: () => api('POST', `/api/partner-invoices/${invoice.id}/dismiss`, { reason }), onSuccess: onChanged });
  return (
    <li className="space-y-1 rounded border border-amber-300 p-3 text-sm dark:border-amber-800">
      <p>
        <a className="font-medium underline" href={`/api/partner-invoices/${invoice.id}/pdf?inline=1`} target="_blank" rel="noreferrer">{invoice.filename}</a>
        {' '}from {invoice.fromAddress ?? 'unknown'}{invoice.partnerName ? ` (${invoice.partnerName})` : ''}, {when(invoice.receivedAt)}
      </p>
      <p className="text-slate-600 dark:text-slate-400">
        {invoice.invoiceNumber ?? 'no number'}, {invoice.invoiceDate ?? 'no date'}, {invoice.amountEur ? eur(invoice.amountEur) : 'no amount'}: {reasonText(invoice.unmatchedReason)}
      </p>
      {isAdmin && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="mr-2 text-slate-600 dark:text-slate-400">Statement</span>
            <select aria-label="Statement" className="rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900" value={statementId} onChange={(e) => setStatementId(e.target.value)}>
              <option value="">choose...</option>
              {detail.data?.candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.partnerName} {c.number ?? ''} {c.coveredMonths.length ? period(c.coveredMonths[0]!) : ''} {eur(c.payoutEur)}
                </option>
              ))}
            </select>
          </label>
          <Button onClick={() => assign.mutate()} disabled={!statementId || assign.isPending}>Assign</Button>
          <Button className="bg-slate-500" onClick={() => setDismissing(!dismissing)}>Not an invoice</Button>
        </div>
      )}
      {dismissing && (
        <form className="flex max-w-2xl items-end gap-2" onSubmit={(e) => { e.preventDefault(); dismiss.mutate(); }}>
          <label className="block flex-1 text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">Reason (the item and its PDF are deleted after the retention period)</span>
            <input className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 dark:border-slate-700 dark:bg-slate-900" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <Button type="submit" disabled={reason.trim().length < 10 || dismiss.isPending}>Dismiss</Button>
        </form>
      )}
      {(assign.error ?? dismiss.error) && <ErrorText>{errorText(assign.error ?? dismiss.error, 'The change failed')}</ErrorText>}
    </li>
  );
}

/**
 * Partner invoices and Lexware bookkeeping (STE-11): open items from the billing mailbox that need a manual
 * assignment, what waits for booking and why, every Lexware voucher with its state, files and payment.
 */
export function InvoicesPage() {
  const isAdmin = useMe().user?.role === 'admin';
  const client = useQueryClient();
  const invoices = useQuery({ queryKey: ['invoices', 'list'], queryFn: () => api<PartnerInvoice[]>('GET', '/api/partner-invoices') });
  const status = useBookkeepingStatus();
  const refresh = () => Promise.all(['invoices', 'statements', 'payments'].map((k) => client.invalidateQueries({ queryKey: [k] })));
  const run = useMutation({ mutationFn: () => api<{ queued: boolean }>('POST', '/api/bookkeeping/run'), onSuccess: refresh });
  const retry = useMutation({ mutationFn: (id: string) => api('POST', `/api/bookkeeping/vouchers/${id}/retry`), onSuccess: refresh });
  const open = invoices.data?.filter((i) => i.status === 'open') ?? [];
  const assigned = invoices.data?.filter((i) => i.status === 'assigned') ?? [];
  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Partner invoices and bookkeeping</h1>
      <p className="max-w-3xl text-sm text-slate-500">
        Partners send their invoice to the billing mailbox; it is matched to the statement by sender and amount (or by the statement mail it answers) when the mail server authenticated the sender (DMARC). An admin accepts every invoice from the mailbox before it is booked. Accepted invoices and confirmed Steam payments are booked in Lexware Office with their files; the payment status is read back daily. Dismissed items and items of unknown senders are deleted after {status.data?.settings.retentionDays ?? 90} days.
      </p>
      {status.data && <BookkeepingBanner status={status.data} />}
      {invoices.isError && <ErrorText>{errorText(invoices.error, 'The invoices could not be loaded')}</ErrorText>}
      <div className="space-y-2">
        <h2 className="font-medium">Open items ({open.length})</h2>
        {open.length === 0 ? <p className="text-sm text-slate-500">Nothing to assign.</p> : <ul className="space-y-2">{open.map((i) => <OpenItem key={i.id} invoice={i} onChanged={refresh} />)}</ul>}
      </div>
      {status.data && status.data.waiting.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-medium">Waiting for booking</h2>
          <ul className="list-disc pl-5 text-sm">
            {status.data.waiting.map((w) => <li key={w.id}>{w.label}: {reasonText(w.reason)}</li>)}
          </ul>
        </div>
      )}
      <div className="space-y-2">
        <h2 className="font-medium">Invoices</h2>
        {assigned.length === 0 ? (
          <p className="text-sm text-slate-500">No invoice received yet.</p>
        ) : (
          <table className="text-sm">
            <thead className="text-left text-slate-500">
              <tr><th className="pr-6 font-normal">Partner</th><th className="pr-6 font-normal">Statement</th><th className="pr-6 font-normal">Invoice</th><th className="pr-6 text-right font-normal">Amount</th><th className="pr-6 font-normal">Check</th><th className="pr-6 font-normal">Accepted</th><th className="font-normal">Lexware</th></tr>
            </thead>
            <tbody>
              {assigned.map((i) => (
                <tr key={i.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="pr-6">{i.partnerName}</td>
                  <td className="pr-6">{i.statementId ? <Link to="/statements/$statementId" params={{ statementId: i.statementId }} className="underline">{i.statementNumber ?? 'statement'}</Link> : '-'}</td>
                  <td className="pr-6">{i.invoiceNumber ?? '-'} {i.invoiceDate ?? ''}</td>
                  <td className="pr-6 text-right tabular-nums">{i.amountEur ? eur(i.amountEur) : '-'}</td>
                  <td className="pr-6"><AmountCheck invoice={i} /></td>
                  <td className={`pr-6 ${i.acceptedAt ? '' : 'font-semibold text-amber-700 dark:text-amber-400'}`}>{i.acceptedAt ? (i.acceptedBy ?? 'yes') : 'waits for accept'}</td>
                  <td className={i.voucher ? VOUCHER_STATUS[i.voucher.status].className : 'text-slate-500'}>{i.voucher ? VOUCHER_STATUS[i.voucher.status].label : 'not booked'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="space-y-2">
        <h2 className="font-medium">Lexware vouchers</h2>
        {status.data?.state && <p className="text-sm text-slate-500">Last round {when(status.data.state.lastRunAt)}, last success {when(status.data.state.lastSuccessAt)}.</p>}
        {isAdmin && <Button onClick={() => run.mutate()} disabled={run.isPending}>Book now</Button>}
        {(status.data?.vouchers ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No voucher yet.</p>
        ) : (
          <table className="text-sm">
            <thead className="text-left text-slate-500">
              <tr><th className="pr-6 font-normal">Voucher</th><th className="pr-6 font-normal">Type</th><th className="pr-6 font-normal">Status</th><th className="pr-6 font-normal">Files</th><th className="pr-6 font-normal">Payment</th><th className="font-normal" /></tr>
            </thead>
            <tbody>
              {status.data!.vouchers.map((v) => (
                <tr key={v.id} className="border-t border-slate-200 dark:border-slate-800">
                  <td className="pr-6">{v.link ? <a className="underline" href={v.link} target="_blank" rel="noreferrer">{v.voucherNumber}</a> : v.voucherNumber}</td>
                  <td className="pr-6">{v.voucherType}</td>
                  <td className={`pr-6 ${VOUCHER_STATUS[v.status].className}`}>{VOUCHER_STATUS[v.status].label}{v.lastError ? `: ${v.lastError}` : ''}</td>
                  <td className="pr-6">{v.files.length}</td>
                  <td className="pr-6">{v.paidOn ? `paid ${v.paidOn}` : (v.lexwareVoucherStatus ?? '-')}</td>
                  <td>{isAdmin && v.status === 'failed' && <Button onClick={() => retry.mutate(v.id)} disabled={retry.isPending}>Retry</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {(run.error ?? retry.error) && <ErrorText>{errorText(run.error ?? retry.error, 'The request failed')}</ErrorText>}
    </section>
  );
}
